import { logger } from "@/lib/logger";
import type { MapPlace } from "@/types/places";
import { coordsToLatLng } from "@/utils/location";
import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";
import { createRoot, type Root } from "react-dom/client";

export interface MapMarkerCallbacks {
  onPlaceSelected: (place: MapPlace) => void;
  onPlaceHover: (place: MapPlace | null) => void;
}

type RenderMarker = (
  place: MapPlace,
  isHovered: boolean,
  isActive: boolean,
  onMouseEnter: () => void,
  onMouseLeave: () => void,
) => React.ReactElement;

export interface UseMapMarkersOptions {
  map: google.maps.Map | null;
  mapsInitialized: boolean;
  places: MapPlace[];
  selectedPlaceId: string | null;
  hoveredPlaceId: string | null;
  callbacks: MapMarkerCallbacks;
  renderMarker: RenderMarker;
}

interface MarkerData {
  marker: google.maps.marker.AdvancedMarkerElement;
  root: Root;
}

function stopMapEvent(e: google.maps.MapMouseEvent) {
  try {
    e?.stop?.();
    e?.domEvent?.stopPropagation?.();
    e?.domEvent?.preventDefault?.();
  } catch {}
}

// Defer unmount to avoid a race with React's render of the same root.
function removeStaleMarkers(
  markers: Map<string, MarkerData>,
  placeIds: Set<string>,
) {
  for (const [id, { marker, root }] of markers) {
    if (!placeIds.has(id)) {
      marker.map = null;
      markers.delete(id);
      queueMicrotask(() => root.unmount());
    }
  }
}

// Renders a placeholder so the marker is visible; the layout effect updates it.
function createPlaceMarker(
  place: MapPlace,
  map: google.maps.Map,
  renderMarker: RenderMarker,
  callbacksRef: RefObject<MapMarkerCallbacks>,
): MarkerData {
  const content = document.createElement("div");
  const root = createRoot(content);

  root.render(
    renderMarker(
      place,
      false,
      false,
      () => {},
      () => {},
    ),
  );

  const marker = new window.google.maps.marker.AdvancedMarkerElement({
    position: coordsToLatLng(place.coords),
    map,
    content,
    title: place.name,
  });

  marker.addListener("gmp-click", (e: google.maps.MapMouseEvent) => {
    stopMapEvent(e);
    callbacksRef.current.onPlaceSelected(place);
  });

  return { marker, root };
}

function createMissingMarkers(
  places: MapPlace[],
  markers: Map<string, MarkerData>,
  map: google.maps.Map,
  renderMarker: RenderMarker,
  callbacksRef: RefObject<MapMarkerCallbacks>,
) {
  for (const place of places) {
    if (!place.coords || markers.has(place.id)) continue;

    try {
      markers.set(
        place.id,
        createPlaceMarker(place, map, renderMarker, callbacksRef),
      );
    } catch (error) {
      logger.error({ error }, "Error creating marker");
    }
  }
}

function attachMarkersToMap(
  markers: Map<string, MarkerData>,
  map: google.maps.Map,
) {
  for (const { marker } of markers.values()) {
    if (marker.map !== map) {
      marker.map = map;
    }
  }
}

/**
 * Manages Google Maps markers for a list of places.
 * Creates markers once per place, updates their display state when needed.
 */
export function useMapMarkers({
  map,
  mapsInitialized,
  places,
  selectedPlaceId,
  hoveredPlaceId,
  callbacks,
  renderMarker,
}: UseMapMarkersOptions): React.RefObject<Map<string, MarkerData>> {
  const markersRef = useRef<Map<string, MarkerData>>(new Map());

  // Store latest callbacks and renderMarker in refs to avoid unnecessary effect re-runs
  const callbacksRef = useRef(callbacks);
  const renderMarkerRef = useRef(renderMarker);

  // Update refs when callbacks or renderMarker change
  useEffect(() => {
    callbacksRef.current = callbacks;
    renderMarkerRef.current = renderMarker;
  }, [callbacks, renderMarker]);

  // Effect: Create/remove markers when places change
  useEffect(() => {
    if (!map || !mapsInitialized) return;

    const markers = markersRef.current;

    removeStaleMarkers(markers, new Set(places.map((p) => p.id)));
    createMissingMarkers(
      places,
      markers,
      map,
      renderMarkerRef.current,
      callbacksRef,
    );
    attachMarkersToMap(markers, map);
  }, [places, map, mapsInitialized]);

  // Unmount cleanup: clear all markers and React roots once.
  useEffect(() => {
    const markers = markersRef.current;
    return () => {
      for (const { marker, root } of markers.values()) {
        marker.map = null;
        queueMicrotask(() => root.unmount());
      }
      markers.clear();
    };
  }, []);

  // LayoutEffect: Update marker display state synchronously after markers exist
  useLayoutEffect(() => {
    if (!map || !mapsInitialized) return;

    for (const place of places) {
      const markerData = markersRef.current.get(place.id);
      if (!markerData) continue;

      const isHovered = hoveredPlaceId === place.id;
      const isActive = selectedPlaceId === place.id;

      markerData.root.render(
        renderMarkerRef.current(
          place,
          isHovered,
          isActive,
          () => callbacksRef.current.onPlaceHover(place),
          () => callbacksRef.current.onPlaceHover(null),
        ),
      );
    }
  }, [places, selectedPlaceId, hoveredPlaceId, map, mapsInitialized]);

  return markersRef;
}
